import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useConcepts } from '@/db/hooks'
import { Button, Empty, PageTitle, SectionTitle } from '@/components/ui'
import { groupByCategory, matchConcept } from '@/lib/learn'
import { FAMILIARITIES, type Familiarity } from '@/kinds/learn/schema'
import { FAMILIARITY_WORD } from '@/lib/learn'
import { AddConceptButton, AddCourseButton, FamiliarityWord, LearnTabs } from './parts'

/** Every concept by category, narrowed by familiarity and by words typed (the way Search matches). */
export const LearnLibraryView = () => {
  const concepts = (useConcepts() ?? []).filter((c) => !c.deleted_at)
  const [q, setQ] = useState('')
  const [only, setOnly] = useState<Familiarity | null>(null)
  const shown = concepts.filter((c) => (!only || c.status.familiarity === only) && matchConcept(c, q))
  const groups = groupByCategory(shown)
  return (
    <div data-testid="learn-library">
      <PageTitle right={<AddConceptButton />}>Learn</PageTitle>
      <LearnTabs />
      {concepts.length === 0 ? (
        <Empty icon="🎓" action={<AddCourseButton />}>
          No concepts yet. Add the design patterns course, or capture one from the Add sheet.
        </Empty>
      ) : (
        <>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search concepts"
            className="w-full min-h-11 mb-3"
            type="search"
            aria-label="Search concepts"
            data-testid="learn-search"
          />
          <div className="flex flex-wrap gap-1.5 mb-4" role="group" aria-label="Familiarity">
            <Button variant={only ? 'chip' : 'chipOn'} onClick={() => setOnly(null)}>
              All
            </Button>
            {FAMILIARITIES.map((f) => (
              <Button
                key={f}
                variant={only === f ? 'chipOn' : 'chip'}
                onClick={() => setOnly(only === f ? null : f)}
                data-testid={`learn-filter-${f}`}
              >
                {FAMILIARITY_WORD[f]}
              </Button>
            ))}
          </div>
          {groups.length === 0 && <Empty>Nothing matches.</Empty>}
          {groups.map(({ category, concepts: rows }) => (
            <section key={category} className="mb-5" data-testid="learn-category">
              <SectionTitle>{category}</SectionTitle>
              <ul className="group-list">
                {rows.map((c) => (
                  <li key={c.id}>
                    <Link
                      to="/learn/concept/$id"
                      params={{ id: c.id }}
                      className="flex items-center gap-3 px-3.5 py-2 min-h-14"
                      data-testid="learn-concept-row"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-medium truncate">{c.spec.name}</span>
                        {c.spec.one_liner && (
                          <span className="block text-xs text-muted truncate">{c.spec.one_liner}</span>
                        )}
                      </span>
                      <FamiliarityWord state={c.status.familiarity} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </div>
  )
}
